export type AppEnvironment = 'production' | 'preview' | 'development'

type EnvLike = Record<string, string | undefined>

// Source of truth for "which environment is this code running in?". Sentry and
// Datadog must use it so both tag production and preview the same way.
//
// Browser: decided by hostname. Server (SSR): preview and production run on the
// same Knative runtime (`K_SERVICE` has the same format in both), but only
// production receives the `OTEL_*` variables injected by the cluster, so
// `OTEL_SERVICE_NAME` is the discriminator (validated with Sentry env probe
// events on 2026-10-28).
const PRODUCTION_HOSTNAME = 'decathlon.com.br'
const PREVIEW_HOSTNAME = 'decathlonstore.preview.vtex.app'

export const getEnvironmentFromHostname = (
  hostname: string
): AppEnvironment | undefined => {
  if (hostname.includes(PRODUCTION_HOSTNAME)) return 'production'
  if (hostname.includes(PREVIEW_HOSTNAME)) return 'preview'

  return undefined
}

export const getEnvironmentFromServerEnv = (
  env: EnvLike
): AppEnvironment | undefined => {
  // Not on the Knative runtime (local dev, build time): let the caller fall back.
  if (!env.K_SERVICE) return undefined

  return env.OTEL_SERVICE_NAME ? 'production' : 'preview'
}

// Webpack replaces `process.env` in the SSR bundle with an empty object, so the
// real runtime env is reached through the global Node `process`.
const getServerEnv = (): EnvLike => {
  try {
    // eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval
    const runtimeProcess = new Function('return process')() as NodeJS.Process

    return runtimeProcess?.env ?? {}
  } catch {
    return {}
  }
}

/** `undefined` when the environment can't be told (callers pick their fallback). */
export const getAppEnvironment = (): AppEnvironment | undefined => {
  if (typeof window !== 'undefined') {
    return getEnvironmentFromHostname(window.location.hostname)
  }

  return getEnvironmentFromServerEnv(getServerEnv())
}

// Datadog dashboards and monitors already use `homolog` for preview.
export const getDatadogEnv = (): string => {
  const environment = getAppEnvironment()

  if (environment === 'preview') return 'homolog'

  return environment ?? 'development'
}
